import { createRouter, createWebHistory } from 'vue-router';
import LoginView from '../views/LoginView.vue';
import WorkspaceShell from '@/features/drivers-workspace/WorkspaceShell.vue';
import store from '@/store';
import { recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery';
import { getDefaultAuthedRoute } from '@/utils/getDefaultAuthedRoute';
import { isDriversRole } from '@/utils/driversRoles';
import { lazyRouteView } from '@/features/drivers-workspace/constants/shellKeepAlive';
import { isSuperAdminRole } from '@/utils/systemRoles';
import { useGlobalSettings } from '@/features/settings/composables/useGlobalSettings';

const withChunkRecovery = (loadFn) => () =>
  loadFn().catch((error) => {
    recoverFromChunkLoadError(error);
    throw error;
  });

const lazyLoadView = (view, cacheName = view) =>
  withChunkRecovery(
    lazyRouteView(
      () => import(/* webpackChunkName: "[request]" */ `../views/${view}.vue`),
      cacheName
    )
  );

const shellMeta = {
  showNavBar: true,
  showFooter: false,
  showCanvasBG: false,
  requiresAuth: true,
  layout: 'drivers-shell'
};

const routes = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { showNavBar: false, showCanvasBG: true, requiresAuth: false }
  },
  {
    path: '/',
    component: WorkspaceShell,
    meta: shellMeta,
    children: [
      {
        path: '',
        redirect: () => getDefaultAuthedRoute(store.state.users?.user)
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: lazyLoadView('DashboardView'),
        meta: {
          ...shellMeta,
          shellTab: 'dashboard',
          bootstrapTabKey: 'clevel',
          title: 'Dashboard',
          requiredAnyPermission: ['dashboard.view.global', 'dashboard.view.department']
        }
      },
      {
        path: 'potential',
        name: 'potential',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-potential" */ '@/features/drivers/views/PotentialView.vue'
              ),
            'PotentialView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'potential',
          bootstrapTabKey: 'potential',
          title: 'Potential'
        }
      },
      {
        path: 'in-progress',
        name: 'in-progress',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-in-progress" */ '@/features/drivers/views/InProgressView.vue'
              ),
            'InProgressView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'in-progress',
          bootstrapTabKey: 'inprogress',
          title: 'In Progress'
        }
      },
      {
        path: 'dq-files',
        name: 'dq-files',
        redirect: { name: 'in-progress', query: { view: 'checklist' } }
      },
      {
        path: 'insurance-claims',
        name: 'insurance-claims',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-insurance-claims" */ '@/features/drivers/views/InsuranceClaimsView.vue'
              ),
            'InsuranceClaimsView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'insurance-claims',
          bootstrapTabKey: 'insclaims',
          title: 'Insurance & Claims'
        }
      },
      {
        path: 'llcs',
        name: 'llcs',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-llcs" */ '@/features/drivers/views/LLCsView.vue'
              ),
            'LLCsView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'llcs',
          bootstrapTabKey: 'llcs',
          title: 'LLCs'
        }
      },
      {
        path: 'active',
        name: 'active',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-active" */ '@/features/drivers/views/ActiveView.vue'
              ),
            'ActiveView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'active',
          bootstrapTabKey: 'active',
          title: 'Active'
        }
      },
      {
        path: 'inactive',
        name: 'inactive',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-inactive" */ '@/features/drivers/views/InactiveView.vue'
              ),
            'InactiveView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'inactive',
          bootstrapTabKey: 'inactive',
          title: 'Inactive'
        }
      },
      {
        path: 'archived',
        name: 'archived',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-archived" */ '@/features/drivers/views/ArchivedView.vue'
              ),
            'ArchivedView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'potential',
          bootstrapTabKey: 'archived',
          title: 'Archived'
        }
      },
      {
        path: 'expirations',
        redirect: { path: '/active', query: { expiring: '90' } }
      },
      {
        path: 'statistics',
        name: 'statistics',
        component: withChunkRecovery(
          lazyRouteView(
            () =>
              import(
                /* webpackChunkName: "workspace-statistics" */ '@/features/drivers/views/StatisticsView.vue'
              ),
            'StatisticsView'
          )
        ),
        meta: {
          ...shellMeta,
          shellTab: 'statistics',
          bootstrapTabKey: 'statistics',
          title: 'Statistics'
        }
      },
      {
        path: 'history',
        name: 'history',
        beforeEnter: () => {
          useGlobalSettings().open('history');
          return getDefaultAuthedRoute(store.state.users?.user);
        },
        component: { template: '<div />' },
        meta: {
          ...shellMeta,
          bootstrapTabKey: 'history',
          title: 'History'
        }
      },
      {
        path: 'settings',
        name: 'settings',
        beforeEnter: () => {
          useGlobalSettings().open();
          return getDefaultAuthedRoute(store.state.users?.user);
        },
        component: { template: '<div />' },
        meta: {
          ...shellMeta,
          bootstrapTabKey: 'settings',
          title: 'Settings'
        }
      },
      {
        path: 'account',
        name: 'account',
        beforeEnter: () => {
          useGlobalSettings().open('account');
          return getDefaultAuthedRoute(store.state.users?.user);
        },
        component: { template: '<div />' },
        meta: { ...shellMeta, title: 'Account' }
      },
      {
        path: 'no-access',
        name: 'no-access',
        component: () => import('../views/NoAccessView.vue'),
        meta: shellMeta
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory('/'),
  routes
});

router.beforeEach(async (to) => {
  if (!store.getters['users/authReady']) {
    await store.dispatch('users/initializeAuth');
  }

  const isLogged = store.state.users.user.isLogged;
  const token = localStorage.getItem('srToken');
  const hasLocalToken = !!token && localStorage.getItem('srIsLogged') === 'true';

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isLogged && !hasLocalToken) {
      return { name: 'login' };
    }

    const roles = store.state.users?.user?.roles || [];
    const required = to.meta.requiredAnyPermission;
    const bootstrapTabKey = to.meta.bootstrapTabKey;
    const allowedTabs = store.state.app?.allowedTabs || [];

    if (bootstrapTabKey && isDriversRole(roles)) {
      if (allowedTabs.length && !allowedTabs.includes(bootstrapTabKey)) {
        store.commit('users/setFlashMessage', 'No access to this page.');
        return { name: 'no-access' };
      }
      return true;
    }

    if (Array.isArray(required) && required.length > 0) {
      if (isSuperAdminRole(roles)) {
        return true;
      }
      const permissions = store.state.users?.user?.permissions || [];
      const hasAny = required.some((code) => permissions.includes(code));
      if (!hasAny) {
        store.commit('users/setFlashMessage', 'No access to this page.');
        return { name: 'no-access' };
      }
    }
    return true;
  }

  if (to.name === 'login' && (isLogged || hasLocalToken)) {
    return getDefaultAuthedRoute(store.state.users?.user);
  }
  return true;
});

router.afterEach((to) => {
  const pageTitle = to.matched
    .slice()
    .reverse()
    .find((record) => record.meta?.title)?.meta?.title;
  document.title = pageTitle ? `${pageTitle} · CRC Drivers` : 'CRC Drivers';

  if (to.meta.showNavBar) {
    store.dispatch('navigation/addToHistory', {
      path: to.path,
      name: to.name,
      meta: to.meta
    });
  }
});

export default router;
